/**
 * Supply-risk scenarios: "what if this country's supply fell by 60%, or
 * this supplier stopped?", answered by the API (POST /scenario-runs) from
 * the campaign's declared answers. These are its shapes, and the shocks'
 * encoding in the address bar.
 * A shock covers rows by country, legal entity or parent company, or any
 * mix of them ("Pacific Marine Ltd's Peruvian origins"), optionally narrowed
 * to some commodities.
 *
 * Each answer row says how many tonnes of one ingredient of one material come
 * from one origin in one country. A shock removes a share of the rows it
 * covers; several shocks compound (two 50% cuts on one row leave 25%). From
 * that: the tonnes lost by commodity, material, legal entity and parent
 * company; the ingredients left with nothing (a single point of failure); and,
 * for each affected ingredient, the other origins the same group has already
 * declared for it, and the other suppliers who already deliver the same
 * ingredient from origins no shock touches — options on paper, not promises.
 *
 * Version 1 takes shocks someone types in. Version 2 adds shocks proposed by
 * outside data (see signals.ts); they arrive in the same `Shock` shape, so
 * nothing here changes.
 */

export interface Shock {
  id: string
  /** Who proposed it: 'manual', or a signal provider's id (version 2). */
  source: string
  label?: string
  /** Each scope left empty covers everything; a shock needs at least one of the three. */
  countryCodes: string[]
  /** Legal entities, by supplier id. */
  supplierIds?: string[]
  /** Whole groups, by name as the rows carry it (ids once the export has them). */
  parentCompanies?: string[]
  /** Narrow it to some commodities (by name, as the rows carry them); empty for all. */
  commodities?: string[]
  /** "Not yet approved": suppliers whose submission is in one of these states. A scope like the others. */
  submissionStatuses?: string[]
  /** 0..1 of the covered volume lost; 1 is a complete stop. */
  reduction: number
}

/** The states a "not yet approved" shock can pick, as people name them; In review is three API states. */
export const APPROVAL_GROUPS: { key: string; label: string; statuses: string[] }[] = [
  { key: 'draft', label: 'Draft', statuses: ['draft'] },
  { key: 'submitted', label: 'Submitted', statuses: ['submitted'] },
  { key: 'returned', label: 'Returned', statuses: ['returned'] },
  { key: 'review', label: 'In review', statuses: ['validation_in_progress', 'validated', 'in_final_review'] },
]

/** "Suppliers not yet approved (draft, submitted, returned, in review)". */
export function describeStatuses(statuses: string[]): string {
  const groups = APPROVAL_GROUPS.filter((g) => g.statuses.some((st) => statuses.includes(st)))
  return `Suppliers not yet approved (${groups.map((g) => g.label.toLowerCase()).join(', ')})`
}

export interface Exposure {
  key: string
  label: string
  detail?: string
  totalT: number
  lostT: number
}

export interface GapLine {
  key: string
  supplier: string
  parent: string
  material: string
  ingredient: string
  countries: string[]
  ingredientT: number
  lostT: number
  /** Origins the group already declares for this ingredient, untouched by the shocks. */
  fallbacks: { origin: string; country: string }[]
  /** Other suppliers delivering this ingredient from untouched origins, most volume first. */
  alternatives: { supplier: string; parent: string; tonnes: number }[]
}

export interface OriginImpact {
  originId: string
  origin: string
  country: string
  lng: number | null
  lat: number | null
  totalT: number
  lostT: number
  /** The share of everything this origin supplies that the scenario removes, across all who use it. */
  reduction: number
}

export interface ScenarioResult {
  totalT: number
  lostT: number
  byCommodity: Exposure[]
  byParent: Exposure[]
  bySupplier: Exposure[]
  byMaterial: Exposure[]
  /** Ingredients that lose all their volume: nothing else feeds them. */
  stopped: GapLine[]
  /** Ingredients that lose some, with what else the group declares for them. */
  reduced: GapLine[]
  origins: OriginImpact[]
}

/** What a shock can be scoped to, largest first: the pickers' choices, from the API with each result. */
export interface ScenarioOptions {
  countries: { code: string; tonnes: number }[]
  commodities: string[]
  suppliers: { id: string; name: string; parent: string; tonnes: number }[]
  parents: { name: string; tonnes: number }[]
}

/** What a run stands on: the campaign, and its submissions (those the viewer may see) by status. */
export interface ScenarioBasis {
  campaign: { id: string; name: string; year: number; status: string }
  submissions: Partial<Record<string, number>>
  draftsIncluded: boolean
  /** Why drafts are in: asked for with the switch, or a "not yet approved" shock reaches them. */
  draftsIncludedBy?: 'request' | 'shock' | null
}

/** A run of `POST /scenario-runs`: the result, how many answer rows it read, the options, and its basis. */
export interface ScenarioRun extends ScenarioResult {
  rows: number
  options: ScenarioOptions
  basis?: ScenarioBasis
}

/**
 * The Scenarios screen for a campaign, with shocks already in it: what the
 * dashboard's "Run a scenario" and its rows' "What if this stopped?" open.
 * The campaign travels in the link, so a shared scenario keeps its basis.
 */
export function scenarioLink(campaignId: string, shocks: Omit<Shock, 'id' | 'source'>[] = []): string {
  const q = new URLSearchParams({ campaign: campaignId })
  if (shocks.length) q.set('shocks', encodeShocks(shocks.map((s, i) => ({ ...s, id: `s${i}`, source: 'manual' }))))
  return `/sourcing/scenarios?${q}`
}

/** A complete stop of some legal entities' supply. */
export const stopSuppliers = (supplierIds: string[]): Omit<Shock, 'id' | 'source'> =>
  ({ countryCodes: [], supplierIds, reduction: 1 })

/**
 * Shocks in the address bar, so a scenario can be shared as a link.
 * Shocks are separated by `|`, fields by `;`, values encoded:
 * `c=PE+CL;r=60` · `s=e-pm-pe;r=100` · `p=Pacific%20Marine%20Group;k=Palm;r=50`.
 * (c countries, s legal entities, p parent companies, k commodities, a submission states for a
 * "not yet approved" shock, r percent lost.) Links made before `a` existed read the same.
 */
export function encodeShocks(shocks: Shock[]): string {
  const list = (key: string, values?: string[]) => (values?.length ? [`${key}=${values.map(encodeURIComponent).join('+')}`] : [])
  return shocks.map((s) => [
    ...list('c', s.countryCodes), ...list('s', s.supplierIds), ...list('p', s.parentCompanies), ...list('k', s.commodities), ...list('a', s.submissionStatuses),
    `r=${Math.round(s.reduction * 100)}`,
  ].join(';')).join('|')
}

export function decodeShocks(text: string | null): Shock[] {
  if (!text) return []
  return text.split('|').flatMap((part, i) => {
    const fields = new Map(part.split(';').map((f) => {
      const at = f.indexOf('=')
      return [f.slice(0, at), f.slice(at + 1)] as const
    }))
    const list = (key: string) => (fields.get(key) ?? '').split('+').filter(Boolean).map(decodeURIComponent)
    const reduction = Number(fields.get('r')) / 100
    const shock: Shock = {
      id: `s${i}`, source: 'manual', countryCodes: list('c'), supplierIds: list('s'), parentCompanies: list('p'),
      commodities: list('k'), reduction: Math.min(1, Math.max(0, reduction)),
      ...(list('a').length ? { submissionStatuses: list('a') } : {}),
    }
    // A shock needs a scope and a figure; anything else in the address is ignored.
    const scoped = shock.countryCodes.length > 0 || list('s').length > 0 || list('p').length > 0 || list('a').length > 0
    return Number.isFinite(reduction) && scoped ? [shock] : []
  })
}
